/**
 * FB65 — THE FAMILY MODULE. One job file, one page.
 *
 * A restoration file is rarely one `jobs` row. `26-1273-WTR` (the mitigation)
 * grows `26-1273-MLD` (mold) and `26-1273-REC` (the rebuild): separate rows,
 * same `YY-NNNN` base, different division suffix. That base IS the family key —
 * there is no join table, no parent pointer, and none is ever to be invented.
 *
 * FB59 made those siblings render as SECTIONS of the job page (a "Mold Job
 * Info" + "Mold Accounting" tab pair per division). This module is the decision
 * that followed: an associated job no longer stands alone at all. The MAIN job
 * is the file; every sibling is reached through its sections, and opening a
 * sibling's own URL redirects to the main job with that division's section
 * already selected.
 *
 * Everything here is pure — no React, no supabase, no DOM — so the three rules
 * that matter can be asserted directly:
 *
 *   1. WHICH JOB IS THE FILE.  `mainJobOf` — the earliest `created_at` in the
 *      family, i.e. the original intake. Every other member is an addition to
 *      it. This is the single definition; nothing computes it independently.
 *   2. WHERE A SIBLING URL GOES.  `familyRedirect` — the main job's number plus
 *      the tab key of that sibling's section, so the redirect lands on the
 *      right panel and the URL that results is itself deep-linkable.
 *   3. WHAT THE JOBS LIST SHOWS.  `groupJobsIntoFamilies` — one entry per base,
 *      every division as a chip, opening the main job.
 *
 * A SOLO JOB IS UNTOUCHED BY ALL OF IT: one member, so it is its own main job,
 * `familyRedirect` returns null, and the list group holds exactly its own row.
 */

import { divisionRank, divisionTabKey } from './top-tabs-model';

/**
 * `26-1273-WTR` → `26-1273`. A number with no division suffix passes through
 * unchanged, which means "this is not a family job number" — never "this is the
 * whole family" (see `hasFamilyBase`).
 *
 * This is the canonical definition. `gantt-model.familyBaseOf` re-exports it,
 * `packages/core/jobGroups.jobBaseNumber` mirrors it for the mobile app, and
 * `functions/api/dates/_family.familyBaseOf` restates it server-side (functions
 * cannot import from `src/`).
 */
export function familyBaseOf(jobNumber: string | null | undefined): string {
  return (jobNumber ?? '').trim().replace(/-[A-Z]{2,4}$/, '');
}

/** `26-1273-MLD` → `MLD`; null when the number carries no division suffix. */
export function familyDivisionOf(jobNumber: string | null | undefined): string | null {
  const m = /-([A-Za-z]{2,4})$/.exec((jobNumber ?? '').trim());
  return m ? m[1].toUpperCase() : null;
}

/**
 * Does this job number parse as `<base>-<DIV>`? Off-format numbers (the ~45
 * migrated oddities like `24-0553-REC - 1st Loss`) have no family to compute,
 * and asking for one would `LIKE`-match unrelated rows.
 */
export function hasFamilyBase(jobNumber: string | null | undefined): boolean {
  const jn = (jobNumber ?? '').trim();
  const base = familyBaseOf(jn);
  return !!base && base !== jn;
}

/** The minimum a row has to tell us to take part in the family rules. */
export interface FamilyMemberRef {
  jobNumber: string;
  division: string | null;
  /** `jobs.created_at`. Null sorts LAST — an unknown intake time never wins. */
  createdAt?: string | null;
}

const ts = (v: string | null | undefined): number => {
  if (!v) return Number.POSITIVE_INFINITY;
  const n = Date.parse(v);
  return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
};

/**
 * THE MAIN JOB — the one job file for the whole family.
 *
 * The original intake: the family member created FIRST. A restoration file
 * starts with one call, and every associated job is something added to that
 * call's file afterwards, so "earliest `created_at`" is not a tie-break dressed
 * up as a rule — it is the definition of which row is the file.
 *
 * Determinism matters more than elegance here (the answer decides where every
 * link in the app lands), so the order is fully specified:
 *   1. earliest `created_at`;
 *   2. rows with no `created_at` last, never first;
 *   3. job number ascending as the final tie-break.
 *
 * Returns null for an empty family. Blank job numbers are ignored.
 */
export function mainJobOf(
  members: readonly FamilyMemberRef[],
): FamilyMemberRef | null {
  let best: FamilyMemberRef | null = null;
  for (const m of members) {
    const jn = (m.jobNumber ?? '').trim();
    if (!jn) continue;
    if (!best) { best = m; continue; }
    // Compared, not subtracted: two unknown timestamps are both Infinity, and
    // Infinity - Infinity is NaN, which would silently keep whichever row the
    // caller happened to list first.
    const a = ts(m.createdAt);
    const b = ts(best.createdAt);
    if (a < b || (a === b && jn.localeCompare(best.jobNumber) < 0)) best = m;
  }
  return best;
}

/** Convenience: is `jobNumber` the main job of the family it belongs to? */
export function isMainJob(
  jobNumber: string | null | undefined,
  members: readonly FamilyMemberRef[],
): boolean {
  const jn = (jobNumber ?? '').trim();
  const main = mainJobOf(members);
  return !!main && !!jn && main.jobNumber === jn;
}

/** The family minus the job named — what the top tab row turns into sections. */
export function siblingsOf(
  members: readonly FamilyMemberRef[],
  jobNumber: string | null | undefined,
): FamilyMemberRef[] {
  const jn = (jobNumber ?? '').trim();
  return members.filter((m) => (m.jobNumber ?? '').trim() && m.jobNumber !== jn);
}

/**
 * Which top tab a division's job number is shown under. `job` is that
 * division's Job Information section; `accounting` its Accounting section.
 * Just `divisionTabKey`, re-exported through the family module so callers have
 * one import for "everything about families".
 */
export function familySectionTab(
  kind: 'job' | 'accounting',
  jobNumber: string,
): string {
  return divisionTabKey(kind, jobNumber);
}

export interface FamilyRedirect {
  /** The main job's number — where the browser goes. */
  jobNumber: string;
  /** The `?tab=` value that selects the requested division's section. */
  tab: string;
}

/**
 * Where `/jobs/26-1273-MLD` should actually land.
 *
 * Null means "stay here": the job IS the main job, the family is unknown, or
 * the number does not parse as a family number at all. Otherwise the caller
 * redirects to the main job with that division's section selected — and because
 * the section lives in the URL's `?tab=`, the result survives a refresh and can
 * be pasted to somebody else.
 *
 * An incoming `tab` is honoured when it names Accounting, so a link that
 * deliberately pointed at the sibling's money lands on the sibling's money
 * rather than its job info. Anything else resolves to the Job Info section,
 * which is what "open this associated job" means.
 */
export function familyRedirect(input: {
  jobNumber: string | null | undefined;
  members: readonly FamilyMemberRef[];
  tab?: string | null;
}): FamilyRedirect | null {
  const jn = (input.jobNumber ?? '').trim();
  if (!jn || !hasFamilyBase(jn)) return null;

  // Only reason about a family we can actually see the requested job in — a
  // half-loaded list must never bounce somebody off a job that is really there.
  if (!input.members.some((m) => (m.jobNumber ?? '').trim() === jn)) return null;

  const main = mainJobOf(input.members);
  if (!main || main.jobNumber === jn) return null;

  const tab = (input.tab ?? '').trim();
  const wantsAccounting =
    tab === 'accounting' || tab === divisionTabKey('accounting', jn);

  return {
    jobNumber: main.jobNumber,
    tab: divisionTabKey(wantsAccounting ? 'accounting' : 'job', jn),
  };
}

// ─── The jobs list: one entry per family ─────────────────────────────────────

export interface JobFamilyGroup<T> {
  /** `26-1273` — the file number the entry is titled with. */
  base: string;
  /** Every division in the family, canonical order (water → mold → rebuild → …). */
  divisions: string[];
  /** The row the entry opens and reads its columns from. */
  main: T;
  /** Every family row known, main first. */
  members: T[];
}

/**
 * Collapse a page of job rows into ONE ENTRY PER FAMILY.
 *
 * Two inputs, because the page and the family are different questions: `rows`
 * is the filtered, ordered, paginated result the user asked for, and `family`
 * is every row belonging to those rows' bases (a sibling created two years
 * later is nowhere near its brother in a `created_at desc` page, so it has to
 * be fetched deliberately rather than hoped for). Entries come back in the
 * order their base first appears in `rows`, so sorting and paging still read
 * the way the user set them.
 *
 * `family` may be empty or partial — a group then holds only what it was given,
 * which for a solo job is exactly its own row.
 */
export function groupJobsIntoFamilies<T>(
  rows: readonly T[],
  family: readonly T[],
  read: (row: T) => FamilyMemberRef,
): JobFamilyGroup<T>[] {
  // Bucket every known row by base, de-duplicated by job number.
  const byBase = new Map<string, Map<string, T>>();
  const add = (row: T) => {
    const ref = read(row);
    const jn = (ref.jobNumber ?? '').trim();
    if (!jn) return;
    const base = familyBaseOf(jn);
    let bucket = byBase.get(base);
    if (!bucket) { bucket = new Map(); byBase.set(base, bucket); }
    if (!bucket.has(jn)) bucket.set(jn, row);
  };
  for (const r of rows) add(r);
  for (const r of family) add(r);

  const out: JobFamilyGroup<T>[] = [];
  const emitted = new Set<string>();

  for (const row of rows) {
    const jn = (read(row).jobNumber ?? '').trim();
    if (!jn) continue;
    const base = familyBaseOf(jn);
    if (emitted.has(base)) continue;
    emitted.add(base);

    const members = [...(byBase.get(base) ?? new Map<string, T>()).values()];
    const mainRef = mainJobOf(members.map(read));
    const main = members.find((m) => read(m).jobNumber === mainRef?.jobNumber) ?? row;

    const divisions: string[] = [];
    for (const m of members) {
      const ref = read(m);
      const code = (ref.division ?? familyDivisionOf(ref.jobNumber) ?? '').toUpperCase();
      if (code && !divisions.includes(code)) divisions.push(code);
    }
    divisions.sort((a, b) => divisionRank(a) - divisionRank(b) || a.localeCompare(b));

    // Main first — the entry's own row leads — then the additions in the same
    // canonical division order the job page's section tabs use, so the list and
    // the page agree about what "the second division" is.
    const rest = members
      .filter((m) => m !== main)
      .sort((x, y) => {
        const rx = read(x);
        const ry = read(y);
        const dx = (rx.division ?? familyDivisionOf(rx.jobNumber) ?? '').toUpperCase();
        const dy = (ry.division ?? familyDivisionOf(ry.jobNumber) ?? '').toUpperCase();
        return divisionRank(dx) - divisionRank(dy) || rx.jobNumber.localeCompare(ry.jobNumber);
      });

    out.push({ base, divisions, main, members: [main, ...rest] });
  }

  return out;
}
